import moment from 'moment';
import { IMovimentacaoPeriodo } from '../../models/movimentacoes-periodos.service.model';
import {
    MOVIMENTACAO_PERIOD_MONTH_OPTIONS,
    MOVIMENTACAO_PERIODO_STATUS_CLASSNAMES,
    MOVIMENTACAO_PERIODO_STATUS_LABELS
} from '../constants/movimentacoes-periodos-ui.constants';
import { formatMovimentacaoMonthNumber } from '../../helpers/movimentacoes-value.helpers';
import { resolveInitialMovimentacaoPeriodo } from './movimentacoes-list-ui.helpers';
import {
    IMovimentacaoPeriodoPresentationItem,
    IMovimentacaoPeriodoTableItem
} from '../models/movimentacoes-periodos-ui.model';

/**
 * Resolve o nome do mês exibido para um período de movimentação.
 */
export function getMovimentacaoPeriodoMonthLabel(month: number): string {
    const option = MOVIMENTACAO_PERIOD_MONTH_OPTIONS.find((item) => item.value === month);

    return option ? option.label : '--';
}

/**
 * Formata a competência do período no padrão MM/AAAA.
 */
export function formatMovimentacaoPeriodoCompetencia(periodo: Pick<IMovimentacaoPeriodo, 'mes' | 'ano'>): string {
    return `${getMovimentacaoPeriodoMonthLabel(periodo.mes)}/${periodo.ano}`;
}

/**
 * Formata a data de encerramento do período para leitura em UI.
 */
export function formatMovimentacaoPeriodoEncerramento(date: string | null | undefined): string {
    if (!date) {
        return '';
    }

    const parts = date.split('-');
    if (parts.length !== 3) {
        return date;
    }

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * Resolve o texto de status exibido para o período.
 */
export function getMovimentacaoPeriodoStatusLabel(situacao: number): string {
    return MOVIMENTACAO_PERIODO_STATUS_LABELS[situacao] || 'Indefinido';
}

/**
 * Resolve a classe visual usada no status do período.
 */
export function getMovimentacaoPeriodoStatusClass(situacao: number): string {
    return MOVIMENTACAO_PERIODO_STATUS_CLASSNAMES[situacao] || 'label-default';
}

/**
 * Monta o resumo textual exibido para um período na UI.
 */
export function formatMovimentacaoPeriodoResumo(
    periodo: IMovimentacaoPeriodo,
    options: { includeFechamento?: boolean } = {}
): string {
    if (options.includeFechamento) {
        const encerramento = formatMovimentacaoPeriodoEncerramento(periodo.encerramentolancamentos) || '-';
        return `Encerramento: ${encerramento}`;
    }

    return '';
}

/**
 * Converte o período bruto para o item de apresentação usado no seletor.
 */
export function buildMovimentacaoPeriodoPresentationItem(
    periodo: IMovimentacaoPeriodo,
    selectedPeriod: IMovimentacaoPeriodo | null = null,
    options: { includeFechamento?: boolean } = {}
): IMovimentacaoPeriodoPresentationItem {
    return {
        id: periodo.movimentacaoperiodo,
        periodo,
        titulo: formatMovimentacaoPeriodoCompetencia(periodo),
        statusLabel: getMovimentacaoPeriodoStatusLabel(periodo.situacao),
        statusClass: getMovimentacaoPeriodoStatusClass(periodo.situacao),
        resumo: formatMovimentacaoPeriodoResumo(periodo, options),
        movimentacoes: typeof periodo.movimentacoes === 'number' ? periodo.movimentacoes : null,
        selecionado: !!selectedPeriod && selectedPeriod.movimentacaoperiodo === periodo.movimentacaoperiodo
    };
}

/**
 * Converte o período bruto para o item exibido na tabela de períodos.
 */
export function buildMovimentacaoPeriodoTableItem(periodo: IMovimentacaoPeriodo): IMovimentacaoPeriodoTableItem {
    const movimentos = typeof periodo.movimentacoes === 'number' ? periodo.movimentacoes : null;
    const movimentosLabel = movimentos === null
        ? '-'
        : `${movimentos} ${movimentos === 1 ? 'movimentação' : 'movimentações'}`;

    return {
        id: periodo.movimentacaoperiodo,
        periodo,
        periodoLabel: formatMovimentacaoPeriodoCompetencia(periodo),
        encerramentoLabel: formatMovimentacaoPeriodoEncerramento(periodo.encerramentolancamentos) || '-',
        movimentosLabel,
        situacao: Number(periodo.situacao),
        situacaoLabel: getMovimentacaoPeriodoStatusLabel(periodo.situacao),
        situacaoClass: getMovimentacaoPeriodoStatusClass(periodo.situacao),
        versao: typeof periodo.versao === 'number' ? periodo.versao : 1
    };
}

/**
 * Resolve o período selecionado com fallback para rota, seleção atual e padrão inicial.
 */
export function resolvePeriodoSelection(
    periodos: IMovimentacaoPeriodo[],
    targetPeriodoId?: string,
    selectedPeriodoAtual: IMovimentacaoPeriodo | null = null
): IMovimentacaoPeriodo | null {
    if (!periodos.length) {
        return null;
    }

    if (targetPeriodoId) {
        const targetPeriodo = periodos.find((periodo) => periodo.movimentacaoperiodo === targetPeriodoId);
        if (targetPeriodo) {
            return targetPeriodo;
        }
    }

    if (selectedPeriodoAtual) {
        const currentPeriodo = periodos.find(
            (periodo) => periodo.movimentacaoperiodo === selectedPeriodoAtual.movimentacaoperiodo
        );
        if (currentPeriodo) {
            return currentPeriodo;
        }
    }

    return resolveInitialMovimentacaoPeriodo(periodos);
}

/**
 * Sugere a próxima competência com base nos períodos já existentes.
 */
export function resolveSugestaoProxPeriodo(periodos: IMovimentacaoPeriodo[]): { mes: number; ano: number } {
    if (!periodos.length) {
        const currentDate = new Date();
        return {
            mes: currentDate.getMonth() + 1,
            ano: currentDate.getFullYear()
        };
    }

    const highestPeriodo = periodos.reduce((currentHighest, periodo) => {
        if (!currentHighest) {
            return periodo;
        }

        if (periodo.ano > currentHighest.ano) {
            return periodo;
        }

        if (periodo.ano === currentHighest.ano && periodo.mes > currentHighest.mes) {
            return periodo;
        }

        return currentHighest;
    }, periodos[0]);

    if (highestPeriodo.mes === 12) {
        return {
            mes: 1,
            ano: highestPeriodo.ano + 1
        };
    }

    return {
        mes: highestPeriodo.mes + 1,
        ano: highestPeriodo.ano
    };
}

/**
 * Monta a data sugerida de encerramento para a competência informada.
 */
export function buildSuggestedClosingDate(ano: number, mes: number): string {
    const lastDay = new Date(ano, mes, 0).getDate();
    const month = formatMovimentacaoMonthNumber(mes);
    const day = lastDay < 10 ? `0${lastDay}` : `${lastDay}`;

    return `${ano}-${month}-${day}`;
}

/**
 * Normaliza datas do formulário de período para o formato esperado pela API.
 */
export function normalizePeriodoDateInput(value: string | Date): string | null {
    if (value instanceof Date) {
        const parsedDate = moment(value);
        return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
    }

    if (typeof value === 'string') {
        const parsedDate = moment(value.trim(), 'YYYY-MM-DD', true);
        return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
    }

    return null;
}
